import { For, Show, type JSX } from "solid-js";
import { Modal } from "../../components/Modal";
import { PanelState } from "../../components/PanelState";
import type { AuditEvent, GatewayKey, Relay } from "../../model";
import type { AsyncPanel } from "../../state/async";
import { formatDate } from "../../utils/format";

interface AuditModalProps {
  panel: AsyncPanel<AuditEvent[]>;
  keys: GatewayKey[];
  relays: Relay[];
  onClose: () => void;
}

const actionLabels: Record<string, string> = {
  "relay.create": "创建 Relay",
  "relay.display_name.update": "修改 Relay 名称",
  "relay.connection.update": "修改 Relay 连接",
  "relay.state.update": "修改 Relay 状态",
  "gateway_key.create": "创建 Gateway Key",
  "gateway_key.state.update": "修改 Gateway Key 状态",
  "gateway_key.limits.update": "修改 Gateway Key limits",
  "gateway_key.eligible_relay.add": "添加 Eligible Relay",
  "gateway_key.eligible_relay.remove": "移除 Eligible Relay",
};

function auditObject(event: AuditEvent, keys: GatewayKey[], relays: Relay[]): { label: string; identifiers: string[] } {
  if (event.subjectType === "gateway_key") {
    const key = keys.find((item) => item.id === event.subjectID);
    if (key) return { label: key.label, identifiers: [key.publicID, key.id] };
  }
  if (event.subjectType === "relay") {
    const relay = relays.find((item) => item.id === event.subjectID);
    if (relay) return { label: relay.displayName, identifiers: [relay.id] };
  }
  return { label: event.subjectType, identifiers: [event.subjectID] };
}

export function AuditModal(props: AuditModalProps): JSX.Element {
  const events = () => props.panel.state().data ?? [];
  return (
    <Modal title="管理操作记录" eyebrow="Administrative Activity" onClose={props.onClose} size="wide">
      <div class="modal-toolbar">
        <p>最近 100 条成功的 Relay Registry 与 Gateway Key 管理操作。</p>
        <button type="button" class="button button--ghost" onClick={() => void props.panel.refresh()}>刷新</button>
      </div>
      <Show
        when={events().length > 0}
        fallback={<div class="empty-state"><strong>暂无管理操作记录</strong><span>成功的管理 mutation 会记录在这里。</span></div>}
      >
        <div class="table-scroll table-scroll--modal">
          <table>
            <thead><tr><th>时间</th><th>操作</th><th>对象</th><th>结果</th></tr></thead>
            <tbody>
              <For each={events()}>
                {(event) => {
                  const object = auditObject(event, props.keys, props.relays);
                  return (
                    <tr>
                      <td>{formatDate(event.createdAtMS)}</td>
                      <td>{actionLabels[event.action] ?? event.action}</td>
                      <td>
                        <span class="audit-object">
                          <strong>{object.label}</strong>
                          <For each={object.identifiers}>{(identifier) => <code>{identifier}</code>}</For>
                        </span>
                      </td>
                      <td>{event.result}</td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
      <PanelState state={props.panel.state()} onRetry={() => void props.panel.refresh()} />
    </Modal>
  );
}
